import { ChromeSideNav, ChromeContent } from 'components/Chrome';
import ContentTitle from 'components/ContentTitle';
import FormField from 'components/FormField';
import SettingsNarrowWrapper from 'components/SettingsNarrowWrapper';
import WorkspaceSettingsNav from 'components/WorkspaceSettingsNav';
import KoalaButton from 'koala/components/Button';
import React, { Fragment, useState } from 'react';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setGlobalModalContent } from 'state/actions/globalUIActions';
import { Webhook, Workspace } from 'types';
import { useInfiniteQuery } from 'react-query';
import * as remoteApi from 'api/remote';
import queryKeys from 'config/queryKeys';
import { AxiosResponse } from 'axios';
import parse from 'parse-link-header';
import KoalaLoader from 'koala/components/Loader';
import WebhookRow from './WebhookRow';

const getNextPage = (response: AxiosResponse<Webhook[]>) => {
  if (response && response.headers && response.headers.link) {
    const links = response.headers.link;
    const parsed = parse(links);
    if (parsed && parsed.next) {
      return parsed.next.page;
    }
  }
  return null;
};

interface Props {
  workspace: Workspace;
}

function WorkspaceSettingsWebhooks(props: Props) {
  const { workspace } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [webhooks, setWebhooks] = useState<AxiosResponse<Webhook[]>[]>([]);
  const [resultsCount, setResultsCount] = useState<string | null>(null);

  const queryKey = [queryKeys.webhooks, workspace.slug];
  const staleTime = queryKeys.staleTime;

  const { isLoading } = useInfiniteQuery(queryKey, remoteApi.fetchWebhooks, {
    getFetchMore: (lastGroup, allGroups) => {
      return getNextPage(lastGroup);
    },
    onSuccess: (response: AxiosResponse<Webhook[]>[]) => {
      setWebhooks(response);
      const resultsCount = response[0].headers['x-total'] ?? '0';
      setResultsCount(resultsCount);
    },
    staleTime,
  });

  const handleAddWebhook = () => {
    dispatch(setGlobalModalContent('webhook::add'));
  };

  return (
    <Fragment>
      <Helmet>
        <title>
          {workspace.name} | {t('workspaceSettingsWebhooks.title')} | Tability
        </title>
      </Helmet>
      <ChromeSideNav>
        <WorkspaceSettingsNav />
      </ChromeSideNav>
      <ChromeContent>
        <SettingsNarrowWrapper>
          <ContentTitle>
            <h1>{t('workspaceSettingsWebhooks.title')}</h1>
          </ContentTitle>
          <FormField>
            <p>{t('workspaceSettingsWebhooks.info')}</p>
          </FormField>
          <FormField>
            <KoalaButton onClick={handleAddWebhook}>{t('workspaceSettingsWebhooks.addWebhook')}</KoalaButton>
          </FormField>

          {isLoading && <KoalaLoader />}

          {!isLoading && (
            <>
              {resultsCount === '0' && <p>{t('workspaceSettingsWebhooks.noWebhooks')}</p>}

              {webhooks.map((group, index) => {
                return (
                  <div key={index}>
                    {group.data.map((webhook: Webhook) => (
                      <WebhookRow key={webhook.id} webhook={webhook} />
                    ))}
                  </div>
                );
              })}
            </>
          )}
        </SettingsNarrowWrapper>
      </ChromeContent>
    </Fragment>
  );
}

export default WorkspaceSettingsWebhooks;
